/* eslint-disable no-unused-vars */
import { color, Common } from '@stockbit/ui';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Image from 'global/Image';
import ModalShare from 'global/ModalShare';
import { effects as chatEffects } from 'lib/entities/chat/slice';
import { Tooltip } from 'antd';
import useTooltip from 'hooks/useTooltip';
import { PROJECT_FOLDER } from 'constants/app';
import StreamTabAnalytics from 'features/stream/analytics/StreamTabAnalytics';
import { getAnalyticsPageSource } from 'utils/source';
import { ModalInsight, ModalLike, ModalTipping } from '../Modals';
import { FooterContainer } from './styled';
import { StreamPostData } from '../../../../@types/stream';
import { effects } from '../slice';
import * as DataCy from './__tests__/constants';

const { Flex, Text, Iconmoon, IconWrapper } = Common;

const LS_TOOLTIP_SHARE = 'tooltipShare';

interface FooterProps {
  post: StreamPostData;
  onComment: (val: any) => any;
  hideLikers?: boolean;
  isFirstPost?: boolean;
  symbol?: string;
}

function Footer({
  post,
  onComment,
  symbol = '',
  hideLikers = false,
  isFirstPost = false,
}: FooterProps) {
  const pageSource = getAnalyticsPageSource();
  const dispatch = useDispatch();

  const { showTooltip: showShareTolltip, onCloseTooltip: onCloseShareTooltip } =
    useTooltip(LS_TOOLTIP_SHARE);

  const [modalShare, showModalShare] = useState<boolean>(false);
  const [modalInsight, showModalInsight] = useState<boolean>(false);
  const [modalTipping, showModalTipping] = useState<boolean>(false);
  const [modalLike, showModalLike] = useState<boolean>(false);

  const { likes, replies, liked, postid } = post;

  // TODO: handle logic in footer
  /**
   * - Handle like post -> button after liked/unliked (DONE)
   * - Open modal conversation
   * - Hide tipjar icon if post.userid === current userid
   */

  const handleLikePost = () => {
    const action = liked ? effects.unlikePost : effects.likePost;
    // @ts-ignore
    dispatch(action(postid));
  };

  const handleShareToChat = async (chatData) => {
    const send = await dispatch(
      chatEffects.sendBulkChat({
        ...chatData,
        attachment_url: '',
        shared_content: {
          type: 'SHARED_CONTENT_TYPE_STREAM',
          id: post.postid,
        },
      }),
    );
    // @ts-ignore
    const { error } = send.payload;

    if (!error) {
      showModalShare(false);
    }
  };

  const handleClickShare = () => {
    if (pageSource) {
      StreamTabAnalytics.common.Action.clickShareButton(pageSource);
    }
    showModalShare(true);
  };

  const handleClickTip = () => {
    if (pageSource) {
      StreamTabAnalytics.common.Action.clickTipping({
        pageSource,
        streamId: post.postid,
        userId: post.userid,
      });
    }
    showModalTipping(true);
  };

  const urlToShare = useMemo(() => {
    const path =
      !PROJECT_FOLDER || PROJECT_FOLDER === '/' ? '/' : `/${PROJECT_FOLDER}/`;
    return `${window.location.origin}${path}post/${post.postid}`;
  }, [post]);

  return (
    <FooterContainer>
      <Flex justify="space-between" align="flex-end">
        <Flex align="flex-end">
          <div>
            {!hideLikers ? (
              <Text
                as="a"
                className="likes-info"
                onClick={() => showModalLike(true)}
              >
                {likes > 0 && `${likes} likes`}
              </Text>
            ) : null}
            <Flex padding="10px 20px 0 0">
              <IconWrapper
                onClick={handleLikePost}
                data-cy={
                  symbol
                    ? DataCy.DATA_CY_FOOTER_LIKE_BUTTON_COMPANY
                    : DataCy.DATA_CY_FOOTER_LIKE_BUTTON
                }
              >
                {liked ? (
                  <Image
                    src="/icon/post-stream/like-fill.svg"
                    alt="Like post filled"
                    style={{ width: '20px' }}
                  />
                ) : (
                  <Image
                    src="/icon/post-stream/like.svg"
                    alt="Like post"
                    style={{ width: '20px' }}
                  />
                )}
              </IconWrapper>
            </Flex>
          </div>
          <Flex padding="0 20px 0 0">
            <Tooltip
              placement="top"
              title={
                <Text
                  color={color.light}
                  family="bold"
                  size="12px"
                  height="22px"
                  onClick={onCloseShareTooltip}
                >
                  Share to friends
                </Text>
              }
              defaultVisible={showShareTolltip && isFirstPost}
              visible={showShareTolltip && isFirstPost}
              overlayClassName="tooltip-overlay-green"
              overlayInnerStyle={{
                background: color.primary,
                padding: '6px 8px',
              }}
              zIndex={99}
              arrowPointAtCenter
              onVisibleChange={(open) => onCloseShareTooltip()}
            >
              <IconWrapper
                onClick={handleClickShare}
                data-cy={
                  symbol
                    ? DataCy.DATA_CY_FOOTER_SHARE_BUTTON_COMPANY
                    : DataCy.DATA_CY_FOOTER_SHARE_BUTTON
                }
              >
                <Image
                  src="/icon/post-stream/share.svg"
                  alt="Share post"
                  style={{ width: '20px' }}
                />
              </IconWrapper>
            </Tooltip>
          </Flex>
          {post.analytics && (
            <IconWrapper
              padding="0 20px 0 0"
              onClick={() => showModalInsight(true)}
            >
              <Iconmoon
                className="icon-new_analytics"
                size="16px"
                color={color.dimgray}
                hoverColor={color.dimgray}
              />
            </IconWrapper>
          )}
          {!post.is_author && (
            <IconWrapper
              padding="0 20px 0 0"
              onClick={handleClickTip}
              data-cy={
                symbol
                  ? DataCy.DATA_CY_FOOTER_TIP_BUTTON_COMPANY
                  : DataCy.DATA_CY_FOOTER_TIP_BUTTON
              }
            >
              <Image
                src="/icon/post-stream/icon-tip.svg"
                alt="Icon Tip"
                style={{ width: '20px' }}
              />
            </IconWrapper>
          )}
        </Flex>

        <Flex onClick={onComment} style={{ alignItems: 'flex-end' }}>
          <Text padding="0 8px 0">{parseInt(replies, 10) > 0 && replies}</Text>
          <IconWrapper>
            <Image
              src="/icon/post-stream/comment.svg"
              alt="Icon Comment New"
              style={{ width: '20px' }}
              data-cy={
                symbol
                  ? DataCy.DATA_CY_STREAM_COMMENT_ICON_COMPANY
                  : DataCy.DATA_CY_STREAM_COMMENT_ICON
              }
            />
          </IconWrapper>
        </Flex>
      </Flex>

      <ModalShare
        post={post}
        sendChat={handleShareToChat}
        title="Share"
        hasCopyButton
        hasRepostButton={post.is_author === 0}
        hasSearchPeople
        hasSocialMedia
        hasShareInput
        urlToShare={urlToShare}
        visible={modalShare}
        onCancel={() => showModalShare(false)}
      />
      <ModalInsight
        visible={modalInsight}
        post={post}
        onCancel={() => showModalInsight(false)}
      />
      <ModalTipping
        streamId={postid}
        userId={post.userid}
        username={post.username}
        avatar={post.avatar}
        visible={modalTipping}
        onCancel={() => showModalTipping(false)}
      />
      <ModalLike
        likes={likes}
        streamId={postid}
        visible={modalLike}
        onCancel={() => showModalLike(false)}
      />
    </FooterContainer>
  );
}

export default Footer;
